import { useEffect, useState } from "react";
import axios from "axios";
import { MenuEditorDialog } from "./MenuEditorDialog";
import { QRSheet } from "./QRSheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UtensilsCrossed, QrCode, Pencil, Loader2, Printer, SlidersHorizontal, Coffee, Wine } from "lucide-react";
import { printMenu } from "./receipt";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MenuAdminPage() {
  const [menu, setMenu] = useState([]);
  const [lounges, setLounges] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rules, setRules] = useState({ coffee: [], glasses: [] });
  const [loading, setLoading] = useState(true);

  const load = () => axios.get(`${API}/menu`).then((r) => { setMenu(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => {
    load();
    axios.get(`${API}/lounges`).then((r) => setLounges(r.data)).catch(() => {});
    axios.get(`${API}/option-rules`).then((r) => setRules({ coffee: r.data.coffee || [], glasses: r.data.glasses || [] })).catch(() => {});
  }, []);

  const toggleRule = (kind, cat) => setRules((r) => {
    const has = r[kind].includes(cat);
    return { ...r, [kind]: has ? r[kind].filter((c) => c !== cat) : [...r[kind], cat] };
  });
  const saveRules = async () => {
    try { await axios.put(`${API}/option-rules`, rules); toast.success("Order options saved"); setRulesOpen(false); }
    catch { toast.error("Could not save options"); }
  };

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-2xl tracking-wide text-slate-900"><UtensilsCrossed size={22} className="text-brand-700" /> Menu</h2>
        <div className="flex gap-2">
          <button data-testid="menu-edit-btn" onClick={() => setEditorOpen(true)}
            className="flex h-10 items-center gap-2 rounded-full bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-600"><Pencil size={15} /> Edit menu</button>
          <button data-testid="menu-options-btn" onClick={() => setRulesOpen(true)}
            className="flex h-10 items-center gap-2 rounded-full border border-sand-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-sand-100"><SlidersHorizontal size={15} /> Order options</button>
          <button data-testid="menu-print-btn" onClick={() => printMenu(menu)}
            className="flex h-10 items-center gap-2 rounded-full border border-sand-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-sand-100"><Printer size={15} /> Print menu</button>
          <button data-testid="menu-qr-btn" onClick={() => setQrOpen(true)}
            className="flex h-10 items-center gap-2 rounded-full border border-sand-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-sand-100"><QrCode size={15} /> QR codes</button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div> : (
        <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
          {menu.map((cat) => (
            <div key={cat.category} className="mb-4 break-inside-avoid rounded-2xl border border-sand-300 bg-white p-4">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-brand-700">{cat.category}</p>
              {cat.items.length === 0 ? <p className="text-xs italic text-slate-400">No items yet</p> : (
                <ul className="space-y-1 text-sm">
                  {cat.items.map((it) => (
                    <li key={it.id} className="flex justify-between gap-2">
                      <span className="text-slate-700">{it.name}</span>
                      <span className="shrink-0 font-semibold text-slate-500">{it.price.toFixed(2)}€</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      <MenuEditorDialog open={editorOpen} onOpenChange={(o) => { setEditorOpen(o); if (!o) load(); }} />
      <QRSheet open={qrOpen} onOpenChange={setQrOpen} lounges={lounges} />
      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto" data-testid="options-rules-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl tracking-wide"><SlidersHorizontal size={18} className="text-brand-700" /> Order options per category</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Choose which categories ask coffee questions (sugar/decaf/milk) or "how many glasses?".</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            {menu.map((cat) => (
              <div key={cat.category} className="flex items-center justify-between gap-2 rounded-xl border border-sand-200 bg-white px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{cat.category}</span>
                <button data-testid={`rule-coffee-${cat.category}`} onClick={() => toggleRule("coffee", cat.category)}
                  className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${rules.coffee.includes(cat.category) ? "bg-amber-500 text-white" : "bg-sand-100 text-slate-500"}`}><Coffee size={12} /> Coffee</button>
                <button data-testid={`rule-glasses-${cat.category}`} onClick={() => toggleRule("glasses", cat.category)}
                  className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${rules.glasses.includes(cat.category) ? "bg-sea text-white" : "bg-sand-100 text-slate-500"}`}><Wine size={12} /> Glasses</button>
              </div>
            ))}
          </div>
          <button data-testid="rules-save-btn" onClick={saveRules} className="mt-2 h-11 w-full rounded-full bg-brand text-sm font-bold text-white hover:bg-brand-600">Save options</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
